import { useCallback, useEffect, useMemo, useState } from "react";
import AdminSidebarShell from "../components/AdminSidebarShell";
import config from "../config";

async function parseJsonResponse(response) {
  const raw = await response.text();
  try {
    return raw ? JSON.parse(raw) : {};
  } catch {
    throw new Error("Server returned a non-JSON response");
  }
}

const AdminUploadDocuments = () => {
  const token = useMemo(() => localStorage.getItem("token"), []);
  const apiBase = config.API_BASE_URL || "http://localhost:5003";

  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState("");
  const [documentCount, setDocumentCount] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [listError, setListError] = useState("");

  const fetchDocuments = useCallback(async () => {
    if (!token) return;

    try {
      setListError("");
      const response = await fetch(`${apiBase}${config.ADMIN.DOCUMENT_LIST}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const payload = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(payload?.error || "Failed to fetch documents");
      }

      const safeDocuments = Array.isArray(payload) ? payload : [];
      setDocuments(safeDocuments);
      setDocumentCount(safeDocuments.length);
    } catch (err) {
      setListError(err.message || "Failed to fetch documents");
    }
  }, [apiBase, token]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleUpload = () => {
    if (!file) {
      setStatus("Please select a file");
      return;
    }

    if (!token) {
      setStatus("Unauthorized");
      return;
    }

    const reader = new FileReader();
    setIsUploading(true);

    reader.onload = async () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      if (!result.startsWith("data:")) {
        setStatus("Invalid file payload");
        setIsUploading(false);
        return;
      }

      try {
        const response = await fetch(`${apiBase}${config.ADMIN.DOCUMENT_UPLOAD}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            fileName: file.name,
            fileData: result
          })
        });

        const data = await parseJsonResponse(response);

        if (!response.ok) {
          setStatus(data.error || "Upload failed");
          return;
        }

        setStatus("Upload successful");
        setFile(null);
        await fetchDocuments();
      } catch {
        setStatus("Server error");
      } finally {
        setIsUploading(false);
      }
    };

    reader.onerror = () => {
      setStatus("Failed to read file");
      setIsUploading(false);
    };

    reader.readAsDataURL(file);
  };

  const handleDelete = async (id) => {
    if (!token || !id) return;

    setIsDeleting(id);
    setStatus("");

    try {
      const response = await fetch(`${apiBase}${config.ADMIN.DOCUMENT_DELETE(id)}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const payload = await parseJsonResponse(response);
      if (!response.ok) {
        setStatus(payload?.error || "Delete failed");
        return;
      }

      setStatus("Document deleted");
      await fetchDocuments();
    } catch {
      setStatus("Server error");
    } finally {
      setIsDeleting("");
    }
  };

  return (
    <AdminSidebarShell activeItem="upload">
      <div style={contentWrapper}>
        <div style={header}>
          <h1 style={title}>Upload Documents</h1>
          <p style={subtitle}>
            Upload PDF, TXT, or DOCX files to build the local knowledge base.
          </p>
        </div>

        <div style={card}>
          <input
            type="file"
            accept=".pdf,.txt,.docx"
            style={inputFile}
            onChange={(event) => setFile(event.target.files?.[0] || null)}
          />

          <button
            type="button"
            style={actionButton}
            onClick={handleUpload}
            disabled={isUploading}
          >
            {isUploading ? "Uploading..." : "Upload Document"}
          </button>

          {status ? <p style={statusText}>{status}</p> : null}
          <div style={statsRow}>
            <span style={statsLabel}>Total documents on server:</span>
            <strong style={statsValue}>{documentCount}</strong>
          </div>
        </div>

        <div style={card}>
          <h3 style={listTitle}>Uploaded Documents</h3>
          {listError ? <p style={errorText}>{listError}</p> : null}
          {!listError && documents.length === 0 ? (
            <p style={emptyText}>No uploaded documents yet.</p>
          ) : null}
          {!listError && documents.length > 0 ? (
            <ul style={uploadList}>
              {documents.map((item) => (
                <li key={item.id} style={uploadItem}>
                  <div style={docMeta}>
                    <span style={fileName}>{item.fileName}</span>
                    <span style={timeText}>
                      {item.uploadedAt ? new Date(item.uploadedAt).toLocaleString() : "-"}
                    </span>
                  </div>
                  <button
                    type="button"
                    style={deleteButton}
                    disabled={isDeleting === item.id}
                    onClick={() => handleDelete(item.id)}
                  >
                    {isDeleting === item.id ? "Deleting..." : "Delete"}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    </AdminSidebarShell>
  );
};

export default AdminUploadDocuments;

const contentWrapper = {
  maxWidth: "1000px",
  margin: "0 auto",
  fontFamily: "system-ui, -apple-system, sans-serif"
};

const header = {
  marginBottom: "32px",
  animation: "fadeUp 0.4s ease-out"
};

const title = {
  fontSize: "28px",
  fontWeight: "700",
  color: "#1e293b",
  margin: "0 0 8px 0"
};

const subtitle = {
  fontSize: "16px",
  color: "#64748b",
  margin: 0
};

const card = {
  background: "#ffffff",
  padding: "24px",
  borderRadius: "16px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
  border: "1px solid #e2e8f0",
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  maxWidth: "760px",
  marginBottom: "16px"
};

const inputFile = {
  border: "1px solid #cbd5e1",
  borderRadius: "10px",
  padding: "12px",
  background: "#f8fafc",
  color: "#1e293b",
  fontSize: "18px"
};

const actionButton = {
  border: "none",
  borderRadius: "10px",
  minHeight: "52px",
  padding: "14px 16px",
  background: "#0E5580",
  color: "#fff",
  fontSize: "18px",
  fontWeight: 600,
  cursor: "pointer"
};

const statusText = {
  margin: "6px 0 0 0",
  color: "#1e293b",
  fontSize: "13px"
};

const statsRow = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginTop: "4px"
};

const statsLabel = {
  fontSize: "13px",
  color: "#475569"
};

const statsValue = {
  fontSize: "16px",
  color: "#0E5580"
};

const listTitle = {
  margin: 0,
  fontSize: "16px",
  color: "#1e293b"
};

const errorText = {
  margin: "6px 0 0 0",
  color: "#b91c1c",
  fontSize: "13px"
};

const emptyText = {
  margin: "4px 0 0 0",
  color: "#64748b",
  fontSize: "13px"
};

const uploadList = {
  listStyle: "none",
  margin: "4px 0 0 0",
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: "10px"
};

const uploadItem = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  border: "1px solid #e2e8f0",
  borderRadius: "10px",
  padding: "10px 12px",
  background: "#f8fafc",
  gap: "12px"
};

const docMeta = {
  display: "flex",
  flexDirection: "column",
  gap: "2px"
};

const fileName = {
  color: "#0f172a",
  fontSize: "14px",
  fontWeight: 600
};

const timeText = {
  color: "#64748b",
  fontSize: "12px"
};

const deleteButton = {
  border: "none",
  borderRadius: "8px",
  padding: "8px 12px",
  background: "#b91c1c",
  color: "#fff",
  fontWeight: 600,
  cursor: "pointer"
};
